// Angular
import {
	Component, 
	Output, 
	EventEmitter,
	ElementRef, 
	ViewChild, 
	HostListener, 
	OnInit
} from '@angular/core';

// Service
import { OrderInboxService } from '../service/order-inbox.service';

// State 
import { Store } from '@ngxs/store';
import { InboxState } from '../state/inbox.state';

// Model
import {
	TrackingClientItem,
	TrackingOption,
	TrackingIdForSearchAPI,
	TrackingClientFilter
} from '../model/tracking-client.model';

// Other
import {
	debounceTime, 
	distinctUntilChanged,
	firstValueFrom, 
	lastValueFrom, 
	map, 
	Subject, 
	Subscription
} from 'rxjs';

@Component({
	selector: 'cv-tracking-client-dropdown',
	templateUrl: './tracking-client-dropdown.component.html',
	styleUrls: ['./tracking-client-dropdown.component.scss']
})
export class TrackingClientDropdownComponent implements OnInit {
	private static readonly BASE_PANEL_WIDTH = 400;
	private static readonly VIEWPORT_GUTTER = 8;

	@Output() trackingClientsChange = new EventEmitter<TrackingClientFilter>();

	@ViewChild('wrapperEl') wrapperEl!: ElementRef<HTMLElement>;
	@ViewChild('triggerEl') triggerEl!: ElementRef<HTMLElement>;

	public placeholder = 'Select tracking and client';
	public panelOpen = false;
	public panelTop = 0;
	public panelLeft = 0;
	public panelWidth = 0;

	public trackingOptions: TrackingOption[] = [];
	public selectedTrackingId: TrackingIdForSearchAPI | '' = '';
	public clientOptions: TrackingClientItem[] = [];
	public selectedForCurrentArr: TrackingClientItem[] = [];
	public selectedForCurrentCount = 0;
	public clientFilter = '';

	private clientFilter$ = new Subject<string>();
	private clientFilterSub?: Subscription;

	constructor(
		private orderInboxService: OrderInboxService,
		private store: Store,
	) { }

	/**
	 * Initializes the component by loading tracking options and hydrating the
	 * previously selected tracking/clients from the store snapshot.
	 */
	async ngOnInit(): Promise<void> {
		await this.loadTrackingOptions();

		const tc = this.store.selectSnapshot(InboxState.selectedTrackingClients);

		this.selectedTrackingId = (tc?.trackingId ?? '') as TrackingIdForSearchAPI | '';
		this.selectedForCurrentArr = [...(tc?.clients ?? [])];
		this.selectedForCurrentCount = this.selectedForCurrentArr.length;

		if (this.selectedTrackingId) {
			await this.loadClientOptions(this.selectedTrackingId);
		}
	}

	/**
	 * Closes the overlay when the user clicks outside the component wrapper.
	 * @param ev Document click mouse event.
	 */
	@HostListener('document:click', ['$event'])
	onDocClick(ev: MouseEvent) {
		if (!this.panelOpen) return;
		const w = this.wrapperEl?.nativeElement;
		if (w && !w.contains(ev.target as Node)) this.closePanel();
	}

	/**
	 * Toggles the panel open/closed.
	 */
	togglePanel() {
		this.panelOpen ? this.closePanel() : this.openPanel();
	}

	/**
	 * Opens the selection panel, wires up the debounced client filter stream,
	 * refreshes client options for the selected tracking (if any), and recalculates
	 * the overlay position.
	 */
	async openPanel(): Promise<void> {
		this.panelOpen = true;

		this.clientFilterSub?.unsubscribe();
		this.clientFilterSub = this.clientFilter$.pipe(
			debounceTime(200), distinctUntilChanged()
		).subscribe(query => this.loadClientFilterOptionsByTrackingId(query));

		if (this.selectedTrackingId) {
			await this.loadClientOptions(this.selectedTrackingId);
		} else {
			this.clientOptions = [];
		}

		this.recalcPanelPosition();
	}

	/**
	 * Closes the selection panel, clears the live filter input and unsubscribes
	 * the filter stream.
	 */
	closePanel() {
		this.panelOpen = false;
		this.clientFilter = '';
		this.clientFilterSub?.unsubscribe();
		this.clientFilterSub = undefined;
	}

	/**
	 * Fetches and normalizes the list of tracking options.
	 */
	private async loadTrackingOptions(): Promise<void> {
		try {
			this.trackingOptions = await firstValueFrom(
				this.orderInboxService.getTrackings().pipe(
					map((trackings) => {
						if (!trackings) return [];
						const filtered = trackings
							.filter((t: any) => String(t.id).toLowerCase() !== 'acuity')
							.map((t: any) => {
								let id = t.id;
								if (String(id).toLowerCase() === 'cmpacuity') id = 'CmpAcuity';
								return { ...t, id };
							});
						filtered.push({ id: 'Manual', title: 'Class Valuation' });
						return filtered as TrackingOption[];
					})
				)
			);
		} catch (e) {
			console.error('Failed to load tracking options', e);
			this.trackingOptions = [];
		}
	}

	/**
	 * Loads client (or organization) options for a given tracking id and optional search query.
	 * @param selectedId Tracking id used to determine which endpoint to call.
	 * @param query Optional client/org search text to filter results server-side.
	 */
	private async loadClientOptions(selectedId: TrackingIdForSearchAPI, query = ''): Promise<void> {
		try {
			this.clientOptions = await this.fetchClientOptions(selectedId, query);
		} catch (e) {
			console.error('Failed to load clients/organizations', e);
			this.clientOptions = [];
		}
	}

	/**
	 * Fetch helper that resolves the correct API (organizations vs. clients)
	 * based on the selected tracking id and returns normalized items.
	 *
	 * @param selectedId Tracking id ('Manaul' uses organizations; others map to client apis).
	 * @param query Optional search text.
	 * @returns Promise of normalized client/organization items.
	 */
	private async fetchClientOptions(selectedId: TrackingIdForSearchAPI, query = ''): Promise<TrackingClientItem[]> {
		try {
			if (selectedId === 'Manual') {
				const orgs = await lastValueFrom(this.orderInboxService.getOrganizations(query));
				return (orgs ?? []).map((o: any) => ({ id: String(o.id), title: String(o.title) }));
			} else {
				const apiId =
					selectedId === 'AppScope' ? 'appscope' :
						selectedId === 'CmpAcuity' ? 'cmpacuity' : '';

				const clients = await lastValueFrom(this.orderInboxService.getClients(apiId, query));
				return (clients ?? []).map((c: any) => ({ id: String(c.id), title: String(c.title) }));
			}
		} catch (error) {
			console.error('Error fetching client/organization options', error);
			throw error;
		}
	}

	/**
	 * Pushes raw user input from the filter textbox into the debounced stream.
	 * @param q Raw filter string typed by the user.
	 */
	onClientFilterInput(q: string) {
		this.clientFilter = q;   
		this.clientFilter$.next(q);
	}

	/**
	 * Reloads client options for the current tracking id using the provided
	 * search term and preserves existing selections in-memory.
	 * @param clientName Server-side query string to filter client/org results.
	 */
	public async loadClientFilterOptionsByTrackingId(clientName: string): Promise<void> {
		if (!this.selectedTrackingId) { this.clientOptions = []; return; }
		await this.loadClientOptions(this.selectedTrackingId, clientName || '');
		// Preserve selection references so chips stay rendered while options list changes.
		this.selectedForCurrentArr = [...this.selectedForCurrentArr];
	}

	/**
	 * Handles tracking dropdown changes
	 */
	async onTrackingChange(): Promise<void> {
		if (!this.selectedTrackingId) {
			this.clientOptions = [];
			this.selectedForCurrentArr = [];
			this.selectedForCurrentCount = 0;
			this.trackingClientsChange.emit({ trackingId: '', clients: [] });
			this.recalcPanelPosition();
			return;
		}

		this.clientFilter = '';
		await this.loadClientOptions(this.selectedTrackingId);

		this.selectedForCurrentArr = [];
		this.selectedForCurrentCount = 0;

		this.trackingClientsChange.emit({
			trackingId: this.selectedTrackingId,
			clients: []
		});

		this.recalcPanelPosition();
	}

	/**
	 * Handles client multiselect changes:
	 * @param next The full array of currently selected client items.
	 */
	onClientsChange(next: TrackingClientItem[]) {
		if (!this.selectedTrackingId) return;

		this.selectedForCurrentArr = [...(next || [])];
		this.selectedForCurrentCount = this.selectedForCurrentArr.length;

		this.trackingClientsChange.emit({
			trackingId: this.selectedTrackingId,
			clients: this.selectedForCurrentArr
		});

		this.recalcPanelPosition();
	}

	/**
	 * Removes a single client chip from the current selection.
	 * If it was the last selected item, clears the selection state.
	 * @param c Client item to remove.
	 */
	removeClient(c: TrackingClientItem) {
		if (this.selectedForCurrentCount <= 1) {
			this.clearClientsOnly();
			this.recalcPanelPosition();
			return;
		}
		const next = (this.selectedForCurrentArr || []).filter(v => v.id !== c.id);
		this.onClientsChange(next);
		this.recalcPanelPosition();
	}

	/**
	 * Clears only the selected clients while keeping the current tracking id.
	 * Emits an empty clients list to the parent.
	 */
	private clearClientsOnly(): void {
		this.selectedForCurrentArr = [];
		this.selectedForCurrentCount = 0;
		this.trackingClientsChange.emit({ trackingId: this.selectedTrackingId || '', clients: [] });
		this.recalcPanelPosition();
	}

	/**
	 * Resets both tracking id and selected clients, clears options, and emits
	 * an empty selection to the parent.
	 */
	clearCurrentTracking(): void {
		this.selectedForCurrentArr = [];
		this.selectedForCurrentCount = 0;
		this.selectedTrackingId = '';
		this.clientOptions = [];
		this.clientFilter = '';
		this.trackingClientsChange.emit({ trackingId: '', clients: [] });
		this.recalcPanelPosition();
	}

	/**
	 * Checks if every currently visible option in the list is also selected.
	 * Useful to render a "Select all" checkbox state.
	 * @returns True if all visible options are selected, false otherwise.
	 */
	areAllVisibleSelected(): boolean {
		const visible = (this.clientOptions ?? []).map(o => String(o.id));
		if (visible.length === 0) return false;
		const selectedIds = new Set((this.selectedForCurrentArr ?? []).map(i => String(i.id)));
		return visible.every(id => selectedIds.has(id));
	}

	/**
	 * Toggles selection of all currently visible options:
	 */
	toggleAllClients(): void {
		const visibleIds = (this.clientOptions ?? []).map(o => String(o.id));
		if (visibleIds.length === 0) return;

		const selectedIds = new Set((this.selectedForCurrentArr ?? []).map(i => String(i.id)));
		const allSelected = visibleIds.every(id => selectedIds.has(id));

		if (allSelected) {
			const next = (this.selectedForCurrentArr ?? []).filter(i => !visibleIds.includes(String(i.id)));
			this.onClientsChange(next);
		} else {
			const needAddIds = visibleIds.filter(id => !selectedIds.has(id));
			const addItems = (this.clientOptions ?? []).filter(o => needAddIds.includes(String(o.id)));
			const next = [...(this.selectedForCurrentArr ?? []), ...addItems];
			this.onClientsChange(next);
		}
	}

	/**
	 * Resolves a human-readable tracking title by id using the current options.
	 * Falls back to the id if no title is found.
	 * @param id Tracking id to resolve.
	 * @returns Title string or the id itself.
	 */
	getTrackingTitleById(id?: string): string {
		if (!id) return '';
		const found = this.trackingOptions.find(t => String(t.id).toLowerCase() === String(id).toLowerCase());
		return found?.title ?? id;
	}

	/**
	 * Chip "x" click handler that prevents the click from toggling the panel
	 * and delegates to the single-item removal logic.
	 * @param ev Original click event from the chip.
	 * @param c Client item to remove.
	 */
	onChipRemove(ev: any, c: TrackingClientItem) {
		(ev?.originalEvent ?? ev)?.stopPropagation?.();
		(ev?.originalEvent ?? ev)?.preventDefault?.();
		this.removeClient(c);
	}

	/**
	 * Clears the client selection for the current tracking and emits the empty
	 * selection to the parent. No-op if there is no tracking or no selection.
	 */
	clearAllForCurrent() {
		if (!this.selectedTrackingId) return;
		if (!this.selectedForCurrentArr?.length) return;
		this.onClientsChange([]); // emit empty selection
	}

	/**
	 * Recalculates the overlay panel's position and width relative to the trigger
	 * and viewport constraints. Runs in the next animation frame to ensure layout
	 * is up to date.
	 */
	private recalcPanelPosition(): void {
		if (!this.panelOpen) return;
		const trigger = this.triggerEl?.nativeElement;
		const wrapper = this.wrapperEl?.nativeElement;
		if (!trigger || !wrapper) return;

		requestAnimationFrame(() => {
			this.panelTop = trigger.offsetHeight;

			this.panelWidth = TrackingClientDropdownComponent.BASE_PANEL_WIDTH;

			const rect = wrapper.getBoundingClientRect();
			const viewportW = document.documentElement.clientWidth;
			const gutter = TrackingClientDropdownComponent.VIEWPORT_GUTTER;

			let left = 0;
			const wouldOverflowRight = rect.left + this.panelWidth > (viewportW - gutter);
			if (wouldOverflowRight) {
				left = wrapper.offsetWidth - this.panelWidth;
				const minLeftByViewport = gutter - rect.left;
				left = Math.max(left, minLeftByViewport);
			}
			this.panelLeft = left;
		});
	}

	/**
	 * TrackBy function for *ngFor over client items to reduce DOM churn.
	 * @param _ Index (unused).
	 * @param c Current client item.
	 * @returns Stable unique key for the item (id preferred, title fallback).
	 */
	trackClient = (_: number, c: TrackingClientItem) => c.id ?? c.title;
}
